import React, { useRef, useState } from "react";
import { getCookie } from "shared/auth/utils/cookieUtils";
import { parseApiError } from "shared/api/client/errorParser";
import { generateHeadsetPairingCode, pairHeadsetWithCode } from "../api/device.api";
import { PAIRING_CODE_LENGTH as CODE_LENGTH, digitsFromPaste } from "../utils/pairingCode";
import VenueProvisioning from "../components/VenueProvisioning";
import "../../../styles/legacy/PairHeadset.css";

const PairHeadsetPage: React.FC = () => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value;

    if (!/^[0-9]?$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const digits = digitsFromPaste(e.clipboardData.getData("text"));
    if (!digits) return;
    e.preventDefault();
    setCode(digits);
    setMessage("");
    setMessageType("");
    inputRefs.current[CODE_LENGTH - 1]?.focus();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !e.currentTarget.value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleGenerateTestCode = async () => {
    setIsGenerating(true);
    setMessage("");
    setMessageType("");
    try {
      const generated = await generateHeadsetPairingCode();
      const digits = digitsFromPaste(generated.code);
      if (!digits) throw new Error("The server returned an unexpected pairing code.");
      setCode(digits);
      setMessage(`Test headset code ${generated.code} generated. Press "Pair Device" to verify pairing.`);
      setMessageType("success");
      inputRefs.current[CODE_LENGTH - 1]?.focus();
    } catch (error) {
      setMessage(parseApiError(error, "Could not generate a test headset code."));
      setMessageType("error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePairDevice = async () => {
    const codeString = code.join("");

    if (codeString.length !== CODE_LENGTH) {
      setMessage("Please enter the complete 6-digit code");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const isSignedIn = Boolean(getCookie("userEmail") || getCookie("userToken"));

      if (!isSignedIn) {
        setMessage("Your session could not be found. Please log in again.");
        setMessageType("error");
        setIsLoading(false);
        return;
      }

      await pairHeadsetWithCode(codeString);

      setMessage("VR headset paired successfully!");
      setMessageType("success");

      setTimeout(() => {
        setCode(Array(CODE_LENGTH).fill(""));
        setMessage("");
        setMessageType("");
      }, 3000);
    } catch (error: any) {
      console.error("VR pairing failed:", error);
      setMessage(
        parseApiError(error, "Failed to pair VR headset. Please check your code.")
      );
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="pair-headset-page">
      <div className="library-viewer-content pair-headset-top">
        <div className="library-viewer-frame">
          <h1 className="pairing">Pair your headset</h1>
          <p className="pairing-content">Pair headset to Poetics Stream with a 6-digit code </p>
          <div className="fill-code-container">
            <h2 className="code-instructions">
              Enter the 6-digit code displayed in your headset, below:
            </h2>
            <div className="code-inputs">
              {Array.from({ length: CODE_LENGTH }).map((_, i) => (
                <input
                  key={i}
                  type="text"
                  maxLength={1}
                  className="digit-input"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  aria-label={`Digit ${i + 1}`}
                  value={code[i]}
                  ref={(el) => {
                    inputRefs.current[i] = el;
                  }}
                  onChange={(e) => handleChange(e, i)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  onPaste={handlePaste}
                  disabled={isLoading}
                />
              ))}
            </div>

            {message && (
              <div
                className={`message-container ${messageType}`}
                style={{
                  margin: "20px 0",
                  padding: "12px",
                  borderRadius: "8px",
                  backgroundColor:
                    messageType === "success" ? "#d4edda" : "#f8d7da",
                  color: messageType === "success" ? "#155724" : "#721c24",
                  textAlign: "center",
                  maxWidth: "400px",
                }}
              >
                {message}
              </div>
            )}

            <button
              type="button"
              className="pair-btn"
              onClick={handlePairDevice}
              disabled={isLoading}
              style={{ cursor: "pointer" }}
            >
              <span className="pair-btn-label">
                {isLoading ? "Pairing..." : "Pair Device"}
              </span>
            </button>
            <button
              type="button"
              className="pair-btn pair-btn--secondary"
              onClick={handleGenerateTestCode}
              disabled={isLoading || isGenerating}
              style={{ cursor: "pointer" }}
            >
              <span className="pair-btn-label">
                {isGenerating ? "Generating..." : "Generate Test Headset Code"}
              </span>
            </button>
            <p className="pair-btn-hint">
              No headset nearby? Generate a code exactly like a headset would, then pair it to verify the flow end-to-end.
            </p>
          </div>
        </div>
      </div>
      <VenueProvisioning />
    </div>
  );
};

export default PairHeadsetPage;
